import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";

import Landing from "@/pages/Landing";
import CustomerLogin from "@/pages/auth/CustomerLogin";
import SellerLogin from "@/pages/auth/SellerLogin";
import SuperAdminLogin from "@/pages/auth/SuperAdminLogin";
import Onboard from "@/pages/Onboard";

import AdminLayout from "@/pages/admin/AdminLayout";
import ShopDashboard from "@/pages/admin/ShopDashboard";
import ShopProducts from "@/pages/admin/ShopProducts";
import ShopAddProduct from "@/pages/admin/ShopAddProduct";
import ShopOrders from "@/pages/admin/ShopOrders";
import ShopCategories from "@/pages/admin/ShopCategories";
import ShopPincodes from "@/pages/admin/ShopPincodes";
import ShopSettings from "@/pages/admin/ShopSettings";
import ShopSubscription from "@/pages/admin/ShopSubscription";
import ShopProfile from "@/pages/admin/ShopProfile";
import ShopAvailability from "@/pages/admin/ShopAvailability";

import SuperLayout from "@/pages/super/SuperLayout";
import SuperDashboard from "@/pages/super/SuperDashboard";
import SuperStores from "@/pages/super/SuperStores";
import SuperOrders from "@/pages/super/SuperOrders";
import SuperCustomers from "@/pages/super/SuperCustomers";
import SuperSubscriptions from "@/pages/super/SuperSubscriptions";
import SuperPayments from "@/pages/super/SuperPayments";

import Storefront from "@/pages/store/Storefront";
import CustomerHome from "@/pages/store/CustomerHome";
import ProductPage from "@/pages/store/ProductPage";
import CartPage from "@/pages/store/CartPage";
import Checkout from "@/pages/store/Checkout";
import OrderConfirm from "@/pages/store/OrderConfirm";
import MyOrders from "@/pages/store/MyOrders";
import MyAddresses from "@/pages/store/MyAddresses";

import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsAndConditions from "@/pages/legal/TermsAndConditions";
import RefundCancellationPolicy from "@/pages/legal/RefundCancellationPolicy";
import ContactUs from "@/pages/legal/ContactUs";

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) {
    const target = role === "shopkeeper" ? "/seller/login"
      : role === "super_admin" ? "/super-admin/login"
      : "/login";
    return <Navigate to={target} replace />;
  }
  // Guard on the user's ACTIVE role (session context), not their roles list.
  // E.g. a shopkeeper currently shopping (active_role=customer) shouldn't get into /admin.
  if (role && user.active_role !== role) {
    // Super-admin impersonating a store is allowed to enter /admin/* routes
    // while retaining their super_admin session.
    if (role === "shopkeeper" && user.active_role === "super_admin"
        && typeof window !== "undefined" && sessionStorage.getItem("qd_impersonate_store")) {
      return children;
    }
    // If they have the role but aren't currently switched into it, send them to login portal.
    const target = role === "shopkeeper" ? "/seller/login"
      : role === "super_admin" ? "/super-admin/login"
      : "/login";
    return <Navigate to={target} replace />;
  }
  return children;
}

function RootRoute() {
  // quickdukaan.in landing — customer login by default. Existing sessions go to
  // the page that matches their ACTIVE role (the portal they last logged into).
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (user?.active_role === "super_admin") return <Navigate to="/super-admin" replace />;
  if (user?.active_role === "shopkeeper") return <Navigate to={user.store_id ? "/admin" : "/onboard"} replace />;
  if (user?.active_role === "customer") return <Navigate to="/home" replace />;
  return <CustomerLogin />;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/QDplatform" element={<Landing />} />
            <Route path="/login" element={<CustomerLogin />} />
            <Route path="/seller/login" element={<SellerLogin />} />
            <Route path="/super-admin/login" element={<SuperAdminLogin />} />
            <Route path="/onboard" element={<Protected role="shopkeeper"><Onboard /></Protected>} />

            {/* Shopkeeper Admin */}
            <Route path="/admin" element={<Protected role="shopkeeper"><AdminLayout /></Protected>}>
              <Route index element={<ShopDashboard />} />
              <Route path="products" element={<ShopProducts />} />
              <Route path="products/new" element={<ShopAddProduct />} />
              <Route path="orders" element={<ShopOrders />} />
              <Route path="categories" element={<ShopCategories />} />
              <Route path="pincodes" element={<ShopPincodes />} />
              <Route path="settings" element={<ShopSettings />} />
              <Route path="subscription" element={<ShopSubscription />} />
              <Route path="profile" element={<ShopProfile />} />
              <Route path="availability" element={<ShopAvailability />} />
            </Route>

            {/* Super Admin */}
            <Route path="/super-admin" element={<Protected role="super_admin"><SuperLayout /></Protected>}>
              <Route index element={<SuperDashboard />} />
              <Route path="stores" element={<SuperStores />} />
              <Route path="orders" element={<SuperOrders />} />
              <Route path="customers" element={<SuperCustomers />} />
              <Route path="subscriptions" element={<SuperSubscriptions />} />
              <Route path="payments" element={<SuperPayments />} />
            </Route>

            {/* Static / legal pages (must come BEFORE /:slug catch-all) */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/refund-cancellation-policy" element={<RefundCancellationPolicy />} />
            <Route path="/contact-us" element={<ContactUs />} />

            {/* Customer routes - storefront */}
            <Route path="/home" element={<CustomerHome />} />
            <Route path="/my-orders" element={<MyOrders />} />
            <Route path="/account/addresses" element={<MyAddresses />} />
            <Route path="/:slug/cart" element={<CartPage />} />
            <Route path="/:slug/checkout" element={<Checkout />} />
            <Route path="/:slug/order/:orderId" element={<OrderConfirm />} />
            <Route path="/:slug/product/:productId" element={<ProductPage />} />
            <Route path="/:slug" element={<Storefront />} />
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
